import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import messages from "../Components/messages";

// Helper function to extract VAT content parts based on country format
const extractVatParts = (
  rawVatValue: string,
  vatPrefix: string
): { alphanumericPart: string; digitsPart: string } => {
  const vatContent = rawVatValue.replace(vatPrefix, "").replace(/[\s.]/g, "");

  if (vatPrefix === "FR") {
    // French format: 2 alphanumeric + 9 digits
    const alphanumericPart = vatContent.slice(0, 2).replace(/[^A-Z0-9]/gi, "");
    const digitsPart = vatContent
      .slice(2)
      .replace(/[^0-9]/g, "")
      .slice(0, 9);
    return { alphanumericPart, digitsPart };
  } else {
    // Belgian format: 10 digits only
    const digitsPart = vatContent.replace(/[^0-9]/g, "").slice(0, 10);
    return { alphanumericPart: "", digitsPart };
  }
};

export const formatVatNumber = (
  value: string,
  vatPrefix: "BE" | "FR"
): string => {
  let clean = value.toUpperCase();
  if (clean.startsWith(vatPrefix)) {
    clean = clean.slice(vatPrefix.length).trim();
  }
  clean = clean.replace(/[\s.]/g, "");

  let formatted = "";
  if (vatPrefix === "FR") {
    clean = onlyAlphaNum(clean);
    formatted = formatCompanyNumberFR(clean);
  } else {
    clean = onlyDigit(clean);
    formatted = formatCompanyNumberBE(clean);
  }
  return `${vatPrefix} ${formatted}`;
};

const companyInfoSchema = (vatPrefix: "BE" | "FR", intl) =>
  z
    .object({
      i_register_as_enterprise: z.boolean().refine((val) => val === true, {
        message: intl.formatMessage(messages.requiredField),
      }),
      company_name: z
        .string()
        .trim()
        .min(1, intl.formatMessage(messages.requiredField)),
      vat_number: z
        .string()
        .trim()
        .min(1, intl.formatMessage(messages.requiredField)),
      address: z
        .string()
        .trim()
        .min(2, intl.formatMessage(messages.requiredField))
        .regex(/\d/, intl.formatMessage(messages.addressMustContainNumber)),
      postcode: z
        .string()
        .trim()
        .regex(/^\d+$/, intl.formatMessage(messages.invalidPostalCode))
        .refine(
          (val) => (vatPrefix === "FR" ? val.length === 5 : val.length === 4),
          {
            message: intl.formatMessage(messages.invalidPostalCode),
          }
        ),
      city: z
        .string()
        .trim()
        .min(1, intl.formatMessage(messages.requiredField)),
    })
    .superRefine((data, ctx) => {
      // VAT Validation
      const normalizedVatPrefix = vatPrefix.toUpperCase();
      const rawVatValue = data.vat_number ? data.vat_number.toUpperCase() : "";

      if (data.vat_number && !rawVatValue.startsWith(normalizedVatPrefix)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: intl.formatMessage(messages.badCompanyNumber),
          path: ["vat_number"],
        });
      } else {
        const { alphanumericPart, digitsPart } = extractVatParts(
          rawVatValue,
          normalizedVatPrefix
        );
        const isVatEmpty = !alphanumericPart && !digitsPart;

        if (isVatEmpty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: intl.formatMessage(messages.requiredField),
            path: ["vat_number"],
          });
        } else if (normalizedVatPrefix === "FR") {
          if (alphanumericPart.length !== 2 || digitsPart.length !== 9) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: intl.formatMessage(messages.badCompanyNumber),
              path: ["vat_number"],
            });
          }
        } else {
          if (digitsPart.length !== 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: intl.formatMessage(messages.badCompanyNumber),
              path: ["vat_number"],
            });
          }
        }
      }
    });

export const createCompanyInvoicingResolver = (
  vatPrefix: "BE" | "FR",
  intl
) => {
  return zodResolver(companyInfoSchema(vatPrefix, intl));
};

export const onlyAlphaNum = (str: string): string =>
  str.replace(/[^a-zA-Z0-9]/g, "");
export const onlyDigit = (str: string): string => str.replace(/\D/g, "");

export const formatCompanyNumberBE = (str: string): string => {
  return str.substring(0, 10).replace(/(\d{4})(\d{3})(\d{3})/, "$1.$2.$3");
};

export const formatCompanyNumberFR = (str: string): string => {
  const value = str.substring(0, 11);
  if (value.length <= 2) {
    return value;
  } else if (value.length < 6) {
    return value.substring(0, 2) + value.substring(2);
  } else if (value.length < 9) {
    return value.replace(/^([A-Z0-9]{2})(\d{3})(\d{0,3})/, "$1$2.$3");
  } else if (value.length < 12) {
    return value.replace(/^([A-Z0-9]{2})(\d{3})(\d{3})(\d{0,3})/, "$1$2.$3.$4");
  }
  return value;
};
